/** WebAuthn PRF key derivation and binary encoding helpers.
 * Portable backups continue to use password encryption via bitcoin-backup.
 */

import { Utils } from "@bsv/sdk";

/**
 * Derive an AES-GCM encryption key from PRF output using HKDF
 *
 * @param prfOutput - The 32-byte PRF output from WebAuthn authentication
 * @returns CryptoKey suitable for AES-GCM encryption/decryption
 */
export async function deriveEncryptionKeyFromPRF(
	prfOutput: ArrayBuffer
): Promise<CryptoKey> {
	// Import PRF output as HKDF key material
	const keyDerivationKey = await crypto.subtle.importKey(
		"raw",
		prfOutput,
		"HKDF",
		false,
		["deriveKey"]
	);

	// Derive AES-GCM key using HKDF
	// Using empty salt and "sigma-backup-encryption" as info
	const encryptionKey = await crypto.subtle.deriveKey(
		{
			name: "HKDF",
			info: new TextEncoder().encode("sigma-backup-encryption"),
			salt: new Uint8Array(0),
			hash: "SHA-256",
		},
		keyDerivationKey,
		{ name: "AES-GCM", length: 256 },
		false, // Not extractable
		["encrypt", "decrypt"]
	);

	return encryptionKey;
}

/**
 * Generate a random PRF salt for a new credential
 * This salt should be stored with the credential and used for all PRF evaluations
 *
 * @returns 32-byte random salt as ArrayBuffer
 */
export function generatePRFSalt(): ArrayBuffer {
	return crypto.getRandomValues(new Uint8Array(32)).buffer;
}

/**
 * Convert ArrayBuffer to base64 string using BSV SDK Utils
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
	const bytes = new Uint8Array(buffer);
	const numberArray: number[] = Array.from(bytes);
	return Utils.toBase64(numberArray);
}

/**
 * Convert base64 string to ArrayBuffer using BSV SDK Utils
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
	const numberArray = Utils.toArray(base64, "base64");
	const bytes = new Uint8Array(numberArray);
	return bytes.buffer;
}

/**
 * Convert ArrayBuffer to base64url string (for WebAuthn credential IDs)
 */
export function arrayBufferToBase64URL(buffer: ArrayBuffer): string {
	const base64 = arrayBufferToBase64(buffer);
	return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * Convert base64url string to ArrayBuffer
 */
export function base64URLToArrayBuffer(base64url: string): ArrayBuffer {
	// Add padding if needed
	let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
	while (base64.length % 4) {
		base64 += "=";
	}
	return base64ToArrayBuffer(base64);
}
